/* nb-cmax-buy.js
   Behaviour pass for the Clean Max buy section. Plain JavaScript, no library,
   scoped per section root so two copies can sit on one page.
   Every price string it writes was rendered by Liquid from a product object
   and parked on a data attribute. This file never formats money.

   CRO A3, 2026-09-29: option rows are now the shared nb-c-option-card
   snippet, driven by nb-ui.js's initOptionGroup module (data-nb-option-group
   -> nb:option-change {key, variantId, galleryImage, price}). This file:
     - keeps the gallery track/dots/thumbs paint+goTo behaviour
     - listens for nb:option-change to drive the gallery (spec §1e), the
       price line, the running total and the CTA
     - reads ?option=<key> on load and preselects that option's radio before
       first paint (spec §1d)
     - drives the warranty add-on toggle button (Complete your setup) */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 900px)';

  function init(root) {
    if (root.dataset.nbReady === 'true') return;
    root.dataset.nbReady = 'true';

    /* ---------- Gallery ---------- */
    var track = root.querySelector('[data-nb-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-nb-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-nb-dot]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-nb-thumb]'));
    var current = 0;

    function paint(index) {
      current = index;
      slides.forEach(function (el, i) { el.classList.toggle('is-active', i === index); });
      dots.forEach(function (el, i) {
        el.classList.toggle('is-active', i === index);
        el.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      thumbs.forEach(function (el, i) {
        el.classList.toggle('is-active', i === index);
        el.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    /* CRO A3, 2026-09-29: slide 0 / thumb 0 are the option-driven hero (see
       nb-cmax-buy.liquid, data-nb-gallery-hero / data-nb-gallery-thumb),
       swapped by URL rather than by index since the three refill options
       point at real product photos, not fixed gallery slots. Kept local to
       this file per the coordinator's note: the page-wide generic gallery
       module in nb-ui.js targets [data-nb-gallery-main] and does not apply
       to this section's markup, so there is no second module to collide
       with here. */
    var galleryHero = root.querySelector('[data-nb-gallery-hero]');
    var galleryThumb0 = root.querySelector('[data-nb-gallery-thumb]');
    function swapHero(url) {
      if (!url) return;
      if (galleryHero) galleryHero.src = url;
      if (galleryThumb0) galleryThumb0.src = url;
    }

    function goTo(index, scroll) {
      if (index < 0 || index >= slides.length) return;
      paint(index);
      if (scroll && track) {
        track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }
    }

    if (track && slides.length) {
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          if (window.matchMedia(DESKTOP).matches) return;
          var width = slides[0].offsetWidth || 1;
          var index = Math.round(track.scrollLeft / width);
          if (index !== current) paint(Math.max(0, Math.min(slides.length - 1, index)));
        });
      }, { passive: true });

      track.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowRight') { event.preventDefault(); goTo(current + 1, true); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(current - 1, true); }
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () { goTo(parseInt(dot.dataset.nbDot, 10), true); });
    });
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () { goTo(parseInt(thumb.dataset.nbThumb, 10), true); });
    });

    /* ---------- Question pills ---------- */
    root.querySelectorAll('[data-nb-question]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        document.dispatchEvent(new CustomEvent('nb:question-open', {
          bubbles: true,
          detail: { key: pill.dataset.nbQuestion, source: root }
        }));
      });
    });

    /* ---------- Option group (spec §1d/§1e/§2) ---------- */
    var optionGroup = root.querySelector('[data-nb-option-group]');
    var warrantyStep = root.querySelector('[data-nb-warranty-step]');
    var warrantyInput = root.querySelector('[data-nb-warranty]');
    var warrantyToggle = root.querySelector('[data-nb-warranty-toggle]');
    var warrantyToggleLabel = root.querySelector('[data-nb-warranty-toggle-label]');
    var priceLine = root.querySelector('[data-nb-price-line]');
    var priceNote = root.querySelector('[data-nb-price-note]');
    var cta = root.querySelector('[data-nb-add]');
    var ctaLabel = root.querySelector('[data-nb-cta-label]');
    var totalValue = root.querySelector('[data-nb-total-value]');
    var feedback = root.querySelector('[data-nb-feedback]');

    var labels = {
      cta: ctaLabel ? ctaLabel.textContent.trim() : '',
      unavailable: root.dataset.unavailableLabel || '',
      adding: root.dataset.addingLabel || '',
      success: root.dataset.successLabel || '',
      error: root.dataset.errorLabel || '',
      hoseAdd: warrantyToggleLabel ? warrantyToggleLabel.textContent.trim() : 'Add',
      hoseAdded: root.dataset.hoseAddedLabel || 'Added'
    };

    function say(message, tone) {
      if (!feedback) return;
      feedback.textContent = message || '';
      feedback.classList.toggle('hidden', !message);
      feedback.classList.toggle('text-cobalt', tone !== 'error');
      feedback.classList.toggle('text-ink', tone === 'error');
    }

    function clearFeedback() { say(''); }

    var lastSelected = null;

    function render(detail) {
      if (!detail) return;
      lastSelected = detail;

      var is24 = detail.key === 'm24';
      if (warrantyStep) {
        warrantyStep.classList.toggle('nb-hide', is24);
        if (is24 && warrantyInput) warrantyInput.checked = false;
      }

      var wanted = !!(warrantyInput && warrantyInput.checked && root.dataset.warrantyAvailable === 'true');
      var basePrice = Number(detail.price) || 0;
      var warrantyPrice = Number(root.dataset.warrantyPriceCents) || 0;
      var totalCents = wanted ? basePrice + warrantyPrice : basePrice;
      /* detail.priceText is the money string Liquid already rendered
         server-side (money_without_trailing_zeros, "$575", the site's own
         format, not "SGD 575"). Used as-is whenever the total equals the
         option's own price; only the warranty-added sum needs a client-side
         reformat, via formatMoney below (same convention, no currency code). */
      var basePriceText = detail.priceText || formatMoney(basePrice);

      if (priceLine) priceLine.textContent = detail.price ? basePriceText : '';
      if (priceNote) priceNote.textContent = detail.note || '';
      if (totalValue) totalValue.textContent = detail.price ? (wanted ? formatMoney(totalCents) : basePriceText) : '';

      var available = !!detail.variantId;
      if (cta) {
        cta.disabled = !available;
        if (ctaLabel) ctaLabel.textContent = available ? labels.cta : labels.unavailable;
      }

      document.dispatchEvent(new CustomEvent('nb:cmax-total-change', {
        bubbles: true,
        detail: {
          total: detail.price ? formatMoney(totalCents) : '',
          available: available,
          variantId: detail.variantId || '',
          selected: detail.label || detail.key || ''
        }
      }));

      /* Gallery-follows-option, spec §1e: galleryImage is a real photo URL
         (Clean365 / Clean730 / machine packshot) swapped onto the hero
         slide + first thumbnail, not a slide index. */
      swapHero(detail.galleryImage);
    }

    /* formatMoney: option cards already render money strings server-side;
       this section's own total/price-line need a plain re-render from the
       cents value nb-ui's option-group module passes through data-nb-option-price.
       Falls back to the raw string if Intl isn't usable client-side. */
    function formatMoney(cents) {
      if (!cents && cents !== 0) return '';
      /* Site convention is a bare symbol, no currency code ("$575", never
         "SGD 575") - matches money_without_trailing_zeros. Symbol only,
         no Intl currency formatting, which always appends the ISO code. */
      var symbol = '$';
      var active = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
      if (active === 'MYR') symbol = 'RM';
      var amount = Math.round(cents / 100);
      try {
        return symbol + amount.toLocaleString('en-US');
      } catch (e) {
        return symbol + amount;
      }
    }

    /* nb:option-change never carries `note` (nb-ui.js's shared dispatch is a
       fixed field list other pages also rely on); read it straight off the
       matching card by key instead of widening that shared event. */
    function noteForKey(key) {
      if (!key) return '';
      var card = optionGroup.querySelector('[data-nb-option-key="' + key.replace(/"/g, '') + '"]');
      return card ? (card.getAttribute('data-nb-option-note') || '') : '';
    }

    if (optionGroup) {
      optionGroup.addEventListener('nb:option-change', function (e) {
        clearFeedback();
        var detail = e.detail || {};
        detail.note = noteForKey(detail.key);
        render(detail);
      });

      /* First paint: read the currently-checked radio's data. */
      var checked = optionGroup.querySelector('input[type="radio"]:checked');
      if (checked) {
        var card = checked.closest('.nb-c-option-card');
        if (card) {
          render({
            key: card.getAttribute('data-nb-option-key'),
            variantId: card.getAttribute('data-nb-option-variant-id'),
            galleryImage: card.getAttribute('data-nb-gallery-image'),
            price: card.getAttribute('data-nb-option-price'),
            priceText: card.getAttribute('data-nb-option-price-text'),
            label: card.getAttribute('data-nb-option-label'),
            note: card.getAttribute('data-nb-option-note')
          });
        }
      }

      /* ---------- ?option=<key> preselect, spec §1d ---------- */
      /* Deferred to a macrotask (setTimeout 0): nb-ui.js's initOptionGroup
         (which owns the radios' `change` listeners) and this section's own
         init() are both wired to DOMContentLoaded, and defer-script order
         does not reliably guarantee nb-ui's listener is attached before
         this file runs its own init(). Pushing the click to the next tick
         lets every DOMContentLoaded handler finish registering first, so
         the click's `change` event always has a listener to reach. */
      try {
        var params = new URLSearchParams(window.location.search);
        var wantedKey = params.get('option');
        if (wantedKey) {
          setTimeout(function () {
            var wantedCard = optionGroup.querySelector('[data-nb-option-key="' + CSS.escape(wantedKey) + '"]');
            var wantedInput = wantedCard ? wantedCard.querySelector('input[type="radio"]') : null;
            if (wantedInput && !wantedInput.checked) {
              wantedInput.click();
            }
          }, 0);
        }
      } catch (e) { /* URLSearchParams / CSS.escape unsupported, skip preselect */ }
    }

    if (warrantyInput) {
      warrantyInput.addEventListener('change', function () {
        clearFeedback();
        if (warrantyToggleLabel) {
          warrantyToggleLabel.textContent = warrantyInput.checked ? labels.hoseAdded : labels.hoseAdd;
        }
        if (lastSelected) render(lastSelected);
      });
    }
    if (warrantyToggle && warrantyInput) {
      warrantyToggle.addEventListener('click', function () {
        warrantyInput.checked = !warrantyInput.checked;
        warrantyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /* ---------- Cart ---------- */
    function refreshCart() {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('[data-nb-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count;
          });
        })
        .catch(function () {});
    }

    function addItems(items, button, labelEl, restore) {
      if (!items.length) return;
      button.disabled = true;
      if (labelEl && labels.adding) labelEl.textContent = labels.adding;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || data.message || 'add failed');
            return data;
          });
        })
        .then(function () {
          say(labels.success, 'ok');
          if (labelEl && restore) labelEl.textContent = restore;
          refreshCart();
        })
        .catch(function () {
          say(labels.error, 'error');
          if (labelEl && restore) labelEl.textContent = restore;
        })
        .then(function () {
          button.disabled = false;
        });
    }

    if (cta) {
      cta.addEventListener('click', function () {
        if (!lastSelected || !lastSelected.variantId) return;
        var items = [{ id: Number(lastSelected.variantId), quantity: 1 }];
        if (warrantyInput && warrantyInput.checked && root.dataset.warrantyAvailable === 'true') {
          items.push({ id: Number(root.dataset.warrantyVariant), quantity: 1 });
        }
        addItems(items, cta, ctaLabel, labels.cta);
      });
    }

    paint(0);
  }

  function boot() {
    document.querySelectorAll('[data-nb-cmax-buy]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-nb-cmax-buy]').forEach(init);
  });
})();
